import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users2, Plus, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { enrollmentStatusMeta } from '../../lib/outreach';
import { nyDateKey } from '../../lib/datetime';
import SendOneOffEmailDialog from './SendOneOffEmailDialog';

// AccountOutreachPanel — Sprint F item 6.7 / §3.10.
// Three actions:
//   1. Enroll in existing campaign (dropdown filtered by targeting match).
//   2. Create custom 1-school campaign (navigates to campaign builder).
//   3. Send one-off email (opens SendOneOffEmailDialog).

export default function AccountOutreachPanel({ account }) {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');
  const [oneOffOpen, setOneOffOpen] = useState(false);

  const load = React.useCallback(() => {
    if (!account?.id) return;
    api.get(`/api/admin/accounts/${account.id}/outreach`).then((d) => {
      setEnrollments(d.enrollments || []);
    }).catch(() => {});
    api.get('/api/admin/outreach/campaigns?status=active').then((d) => {
      setCampaigns(d.campaigns || []);
    }).catch(() => {});
  }, [account?.id]);
  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!selectedCampaign) return;
    setEnrolling(true); setError('');
    try {
      await api.post(`/api/admin/outreach/campaigns/${selectedCampaign}/enroll`, {
        accountIds: [account.id],
      });
      load();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Enroll failed');
    } finally {
      setEnrolling(false);
    }
  };

  const startCustomCampaign = () => {
    navigate(`/admin/outreach?new_custom_for=${account.id}`);
  };

  return (
    <div data-testid="account-outreach-panel" className="space-y-5">
      {account?.outreachOptedOutAt && (
        <div
          data-testid="account-outreach-opted-out"
          className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 text-[12px] text-amber-300"
        >
          This account opted out of outreach on {nyDateKey(account.outreachOptedOutAt)}.
        </div>
      )}

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
          <Users2 className="w-3 h-3" />
          Enrollments
        </div>
        {enrollments.length === 0 ? (
          <div className="text-[12px] text-gray-500">Not enrolled in any campaigns.</div>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {/* GET /api/admin/accounts/:id/outreach returns
                { enrollments: [{ enrollment, campaign }] } — both nested objects
                are serialized (camelCase) by functions/api/_lib/serializers.js. */}
            {enrollments.map(({ enrollment, campaign }) => {
              if (!enrollment) return null;
              const em = enrollmentStatusMeta(enrollment.status);
              const campaignId = campaign?.id || enrollment.campaignId;
              return (
                <li
                  key={enrollment.id}
                  data-testid="account-outreach-enrollment-row"
                  className="py-2 flex items-center justify-between gap-3 text-[13px]"
                >
                  <div className="min-w-0">
                    <Link to={`/admin/outreach/${campaignId}`} className="text-white hover:text-[#B0FC38]">
                      {campaign?.name || '—'}
                    </Link>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      step {enrollment.currentStep ?? 0}
                      {enrollment.nextSendAt && ` · next ${nyDateKey(enrollment.nextSendAt)}`}
                      {enrollment.lastSentAt && ` · last sent ${nyDateKey(enrollment.lastSentAt)}`}
                    </div>
                  </div>
                  <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${em.tone}`}>
                    {em.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">Three actions</div>

        <div className="flex items-center gap-2">
          <select
            data-testid="account-outreach-enroll-select"
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          >
            <option value="">Select a campaign to enroll in…</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button
            type="button"
            data-testid="account-outreach-enroll"
            onClick={enroll}
            disabled={enrolling || !selectedCampaign}
            className="px-3 py-2 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] hover:bg-[#9BE22E] disabled:opacity-50"
          >
            <Plus className="inline w-3 h-3 mr-1" /> Enroll
          </button>
        </div>

        <button
          type="button"
          data-testid="account-outreach-custom-campaign"
          onClick={startCustomCampaign}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] text-[12px] text-gray-300 hover:text-white hover:border-white/[0.15]"
        >
          <Plus className="w-3 h-3" /> Create custom 1-school campaign for {account?.name}
        </button>

        <button
          type="button"
          data-testid="account-outreach-one-off"
          onClick={() => setOneOffOpen(true)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E]"
        >
          <Mail className="w-3 h-3" /> Send one-off email
        </button>

        {error && <div className="text-[12px] text-red-300/90">{error}</div>}
      </div>

      <SendOneOffEmailDialog
        open={oneOffOpen}
        accountId={account?.id}
        onClose={() => setOneOffOpen(false)}
        onSent={() => { setOneOffOpen(false); load(); }}
      />
    </div>
  );
}
